const CONSENT_STORAGE_KEY = 'foth_analytics_consent';
const GTM_ID = process.env.REACT_APP_GTM_ID || '';
// Measurement IDs must be provided by the deployment environment. Never ship
export const GA4_ID = process.env.NODE_ENV === 'test'
  ? (process.env.REACT_APP_GA4_MEASUREMENT_ID || '')
  : (process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-8EVTEFLFR4');
// Keep the event contract allowlisted. A denylist is too easy to bypass when
// a future caller introduces a new field containing personal or financial data.
const ALLOWED_PROPERTY_KEYS = new Set([
  'page_path',
  'page_location',
  'page_title',
  'user_role',
  'locale',
  'platform',
  'app_version',
  'source',
  'medium',
  'campaign',
  'flow',
  'status',
  'error_code',
  'collab_type',
  'method',
]);

let gtmLoaded = false;

const canUseBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const SENSITIVE_PATH_PATTERNS = [
  [/^\/i\/[^/]+/, '/i/[invite]'],
  [/^\/reset-password\/[^/]+\/[^/]+/, '/reset-password/[user]/[token]'],
  [/^\/profile\/[^/]+/, '/profile/[username]'],
  [/^\/partners\/[^/]+/, '/partners/[code]'],
  [/^\/affiliate\/[^/]+/, '/affiliate/[code]'],
];

export const getSafePagePath = (locationLike = canUseBrowser() ? window.location : {}) => {
  const pathname = typeof locationLike.pathname === 'string' && locationLike.pathname.startsWith('/')
    ? locationLike.pathname
    : '/';
  const redactedPath = SENSITIVE_PATH_PATTERNS.reduce(
    (safePath, [pattern, replacement]) => safePath.replace(pattern, replacement),
    pathname,
  );
  return redactedPath.replace(/\/\d+(?=\/|$)/g, '/[id]').replace(
    /\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi,
    '/[id]',
  ).slice(0, 200);
};

export const getSafePageLocation = (locationLike = canUseBrowser() ? window.location : {}) => {
  const origin = typeof locationLike.origin === 'string'
    ? locationLike.origin
    : canUseBrowser() ? window.location.origin : '';
  return `${origin}${getSafePagePath(locationLike)}`.slice(0, 300);
};

export const isMeasurementConfigured = () => Boolean(GTM_ID || GA4_ID);

export const getAnalyticsConsent = () => {
  if (!canUseBrowser()) return null;
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch (error) {
    return null;
  }
};

const pushConsent = (value) => {
  if (!canUseBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = (...args) => window.dataLayer.push(args);
  }
  window.gtag('consent', 'update', {
    analytics_storage: value === 'granted' ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
};

export const isAdminSuperuser = (user = null, pathname = (canUseBrowser() ? window.location.pathname : '')) => {
  if (typeof pathname === 'string') {
    if (pathname.startsWith('/console') || pathname.startsWith('/admin')) {
      return true;
    }
  }
  if (user) {
    if (user.is_superuser || user.is_staff || user.user_type === 'admin') {
      return true;
    }
  }
  if (canUseBrowser()) {
    try {
      if (window.localStorage.getItem('foth_is_admin_superuser') === 'true') {
        return true;
      }
    } catch (e) {}
  }
  return false;
};

export const syncAdminSuperuserState = (user = null) => {
  if (!canUseBrowser()) return;
  const isSuperuser = Boolean(
    user && (user.is_superuser || user.is_staff || user.user_type === 'admin')
  );
  try {
    if (isSuperuser) {
      window.localStorage.setItem('foth_is_admin_superuser', 'true');
      if (GA4_ID) window[`ga-disable-${GA4_ID}`] = true;
    } else if (user) {
      window.localStorage.removeItem('foth_is_admin_superuser');
      if (!window.location.pathname.startsWith('/console') && !window.location.pathname.startsWith('/admin')) {
        if (GA4_ID) delete window[`ga-disable-${GA4_ID}`];
      }
    }
  } catch (e) {}
};

export const hasUserOptedOut = () => getAnalyticsConsent() === 'denied';

export const loadMeasurement = (user = null) => {
  if (!canUseBrowser() || !isMeasurementConfigured()) return false;
  if (isAdminSuperuser(user)) {
    if (GA4_ID) window[`ga-disable-${GA4_ID}`] = true;
    return false;
  }
  if (hasUserOptedOut()) {
    if (GA4_ID) window[`ga-disable-${GA4_ID}`] = true;
    return false;
  }
  if (gtmLoaded) return true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || ((...args) => window.dataLayer.push(args));
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
  if (GTM_ID) {
    window.dataLayer.push({
      page_path: getSafePagePath(),
      page_location: getSafePageLocation(),
    });
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    pushConsent('granted');

    const existingScript = document.querySelector(`script[data-foth-gtm="${GTM_ID}"]`);
    if (!existingScript) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
      script.dataset.fothGtm = GTM_ID;
      document.head.appendChild(script);
    }
  } else if (GA4_ID) {
    window.gtag('js', new Date());
    window.gtag('config', GA4_ID, {
      send_page_view: false,
      page_path: getSafePagePath(),
      page_location: getSafePageLocation(),
    });
    pushConsent('granted');

    const existingScript = document.querySelector(`script[data-foth-ga4="${GA4_ID}"]`)
      || document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA4_ID}"]`);
    if (!existingScript) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
      script.dataset.fothGa4 = GA4_ID;
      document.head.appendChild(script);
    }
  }

  gtmLoaded = true;
  return true;
};

export const trackPageView = ({ pathname, user = null, title = '' }) => {
  if (!canUseBrowser() || !isMeasurementConfigured()) return false;
  if (isAdminSuperuser(user, pathname)) {
    if (GA4_ID) window[`ga-disable-${GA4_ID}`] = true;
    return false;
  }
  if (hasUserOptedOut()) {
    if (GA4_ID) window[`ga-disable-${GA4_ID}`] = true;
    return false;
  }

  loadMeasurement(user);

  const safeLocation = { pathname: pathname || window.location.pathname, origin: window.location.origin };
  const page = getSafePagePath(safeLocation);
  const safeProperties = {
    page_path: page,
    page_location: getSafePageLocation(safeLocation),
    page_title: title || document.title,
    user_role: user?.user_type || 'anonymous',
    locale: document.documentElement.lang || 'en',
    platform: 'web',
  };

  if (GTM_ID) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'page_view', ...safeProperties });
  } else if (GA4_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', safeProperties);
  }
  return true;
};

export const sanitizeProperties = (properties = {}) => Object.entries(properties).reduce((result, [key, value]) => {
  const normalizedKey = String(key).toLowerCase();
  if (!ALLOWED_PROPERTY_KEYS.has(normalizedKey) || value === undefined || value === null) return result;
  if (typeof value === 'string') result[normalizedKey] = value.slice(0, 100);
  else if (typeof value === 'number' || typeof value === 'boolean') result[normalizedKey] = value;
  return result;
}, {});

export const track = (eventName, properties = {}, user = null) => {
  if (!canUseBrowser() || !isMeasurementConfigured()) return false;
  if (isAdminSuperuser(user)) {
    if (GA4_ID) window[`ga-disable-${GA4_ID}`] = true;
    return false;
  }
  if (hasUserOptedOut()) return false;
  if (process.env.NODE_ENV === 'test' && eventName !== 'page_view' && getAnalyticsConsent() !== 'granted') return false;
  if (!/^[a-z][a-z0-9_]{0,39}$/.test(eventName)) return false;
  loadMeasurement(user);
  const safeProperties = {
    ...sanitizeProperties(properties),
    // Override the browser's current URL so query-string credentials/tokens
    // cannot be attached to direct GA4 events or consumed by GTM variables.
    page_location: getSafePageLocation(),
  };
  if (GTM_ID) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...safeProperties });
  } else if (GA4_ID && typeof window.gtag === 'function') {
    window.gtag('event', eventName, safeProperties);
  }
  return true;
};

export const setAnalyticsConsent = (value) => {
  if (!canUseBrowser() || !['granted', 'denied'].includes(value)) return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, value);
    if (value === 'denied' && GA4_ID) {
      window[`ga-disable-${GA4_ID}`] = true;
    } else if (value === 'granted' && GA4_ID) {
      if (!window.location.pathname.startsWith('/console') && !window.location.pathname.startsWith('/admin') && window.localStorage.getItem('foth_is_admin_superuser') !== 'true') {
        delete window[`ga-disable-${GA4_ID}`];
      }
    }
  } catch (error) {
    // Analytics remains disabled if the browser blocks local storage.
  }
  pushConsent(value);
  if (value === 'granted') loadMeasurement();
};

export const __resetMeasurementForTests = () => {
  gtmLoaded = false;
};
